import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Mail,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import AutheraLogo from "@/components/AutheraLogo";
import { api } from "@/lib/api";

/**
 * Public Demo Lead Capture
 *
 * Visitor enters their email and we send them a sample Authera certificate
 * email pointing at a real on-chain receipt. Used for top-of-funnel demos —
 * NO dashboard access required and the lead is stored under `demo_leads`
 * for follow-up. Three lines of defence are wired up against abuse:
 *  - honeypot field (input named `website`, must stay empty)
 *  - per-IP rate limit on the backend (3 / hour, 10 / day)
 *  - email format + disposable-domain block on the backend
 */
export default function Demo() {
  const [email, setEmail] = useState("");
  // Honeypot — invisible to humans, irresistible to naive bots.
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { sample_certificate_id, verify_url }
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const resp = await api.post("/public/demo/lead", {
        email: email.trim(),
        website,
      });
      setSuccess({
        sample_certificate_id: resp.data?.sample_certificate_id || null,
        verify_url: resp.data?.verify_url || null,
      });
      toast.success("Sample certificate is on its way to your inbox.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not send the demo. Please try again shortly.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="demo-page"
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 bg-gray-50/40"
    >
      <div className="w-full max-w-lg">
        <div className="text-center mb-7">
          <div className="flex justify-center">
            <AutheraLogo size="md" testid="demo-brand-logo" />
          </div>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0055FF]">
            <Sparkles size={11} strokeWidth={2.5} />
            Public Demo
          </div>
          <h1 className="mt-2 font-display font-extrabold tracking-tight text-gray-900 text-3xl sm:text-4xl leading-[1.1]">
            Try Verification
          </h1>
          <p className="mt-3 text-[15px] text-gray-600 max-w-md mx-auto leading-relaxed">
            Drop your email and we'll send you a sample Authera Digital
            Certificate — real on-chain record, scannable QR, and a public
            verification link you can share.
          </p>
        </div>

        {success ? (
          <div
            data-testid="demo-success"
            className="bg-white border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden"
          >
            <div className="h-[3px] bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
            <div className="px-7 py-8 sm:px-9 sm:py-10 text-center">
              <span className="inline-flex h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center">
                <CheckCircle2 size={26} strokeWidth={2.2} />
              </span>
              <h2 className="mt-4 font-display font-bold text-xl text-gray-900">
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                A sample Authera certificate is on its way. Open the email,
                scan the QR code, or click the verification link — both
                resolve to the same public certificate page below.
              </p>

              {success.verify_url && (
                <a
                  href={success.verify_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="demo-success-verify-link"
                  className="mt-6 inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-[#0044CC] transition"
                >
                  Open Verification Page
                  <ExternalLink size={14} />
                </a>
              )}

              {success.sample_certificate_id && (
                <div className="mt-5 text-[11px] font-mono text-gray-500">
                  Certificate ID: {success.sample_certificate_id}
                </div>
              )}

              <div className="mt-7 pt-6 border-t border-gray-100 text-[12px] text-gray-500">
                Email didn't arrive within a couple minutes? Check spam or{" "}
                <button
                  type="button"
                  onClick={() => {
                    setSuccess(null);
                    setEmail("");
                  }}
                  data-testid="demo-resend"
                  className="text-[#0055FF] font-medium hover:underline"
                >
                  try a different address
                </button>
                .
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="demo-form"
            className="bg-white border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden"
            // Spam protection layer 3: no autocomplete on the form so basic
            // form-fillers don't try the honeypot.
            autoComplete="off"
          >
            <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />
            <div className="px-7 py-8 sm:px-9 sm:py-10">
              <label
                htmlFor="demo-email"
                className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5"
              >
                Your email
              </label>
              <div className="relative">
                <Mail
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  id="demo-email"
                  data-testid="demo-email-input"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
                />
              </div>

              {/* Honeypot — visually hidden + a11y hidden, but bots fill it */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label htmlFor="demo-website">Website</label>
                <input
                  id="demo-website"
                  data-testid="demo-honeypot"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              {error && (
                <div
                  data-testid="demo-error"
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="demo-submit"
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition disabled:opacity-60 disabled:cursor-wait"
              >
                {submitting ? "Sending sample…" : "Send me a sample certificate"}
                <ArrowRight size={14} />
              </button>

              <ul className="mt-6 grid grid-cols-1 gap-2 text-[12px] text-gray-500">
                <li className="flex items-center gap-2">
                  <ShieldCheck size={13} className="text-[#0055FF]" />
                  Real on-chain certificate (Polygon Amoy)
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck size={13} className="text-[#0055FF]" />
                  Scannable QR + public verification link
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck size={13} className="text-[#0055FF]" />
                  We'll never share your email
                </li>
              </ul>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-[12px] text-gray-500">
          Already have a certificate ID?{" "}
          <Link
            to="/verify"
            data-testid="demo-to-verify"
            className="text-[#0055FF] font-medium hover:underline"
          >
            Look it up directly
          </Link>
          .
        </div>
      </div>
    </div>
  );
}
